import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatError } from "../lib/api";
import { Search, Loader2, X, RefreshCw, GraduationCap, Check, AlertTriangle } from "lucide-react";

/**
 * Searchable multi-select of LearnWorlds courses.
 * Search is delegated to the backend (`/lms/courses?q=`) which caches the catalog.
 */
export default function LmsCourseSelect({ selected, onChange }) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const load = useCallback(async (q, refresh = false) => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/lms/courses", { params: { q, refresh }, timeout: 60000 });
      setItems(data.items || []);
      setTotal(data.total || 0);
      if (!q) setCatalogTotal(data.total || 0);
    } catch (e) {
      setErr(formatError(e));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(query), query ? 350 : 0);
    return () => clearTimeout(t);
  }, [query, load]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = (course) => {
    const exists = selected.some((s) => s.id === course.id);
    onChange(exists ? selected.filter((s) => s.id !== course.id) : [...selected, course]);
  };

  return (
    <div data-testid="lms-course-select">
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-2 mb-3" data-testid="lms-selected-list">
          {selected.map((c) => (
            <li
              key={c.id}
              className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-slate-900 text-white text-xs font-semibold rounded-sm"
              data-testid={`lms-selected-chip-${c.id}`}
            >
              <GraduationCap size={12} className="opacity-70" />
              <span className="max-w-[240px] truncate">{c.title}</span>
              <button
                type="button"
                onClick={() => toggle(c)}
                className="opacity-60 hover:opacity-100"
                title="Retirer"
                data-testid={`lms-remove-${c.id}`}
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="relative" ref={wrapRef}>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onFocus={() => setOpen(true)}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              placeholder="Rechercher un cours dans LearnWorlds (mot-clé)…"
              data-testid="lms-course-search-input"
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-300 rounded-sm bg-white focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
            />
            {loading && (
              <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
            )}
          </div>
          <button
            type="button"
            onClick={() => load(query, true)}
            title="Recharger le catalogue depuis LearnWorlds"
            data-testid="lms-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-slate-300 rounded-sm text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw size={13} /> Recharger
          </button>
        </div>

        {open && (
          <div
            className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-300 rounded-sm shadow-lg max-h-[320px] overflow-y-auto"
            data-testid="lms-course-dropdown"
          >
            {err ? (
              <div className="px-3 py-3 text-xs text-[#b91c1c] flex items-start gap-2" data-testid="lms-course-error">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {err}
              </div>
            ) : loading ? (
              <div className="px-3 py-3 text-xs text-slate-500">Chargement du catalogue…</div>
            ) : items.length === 0 ? (
              <div className="px-3 py-3 text-xs text-slate-500" data-testid="lms-course-empty">
                Aucun cours ne correspond à « {query} ».
              </div>
            ) : (
              <ul>
                {items.map((c) => {
                  const isSel = selected.some((s) => s.id === c.id);
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => toggle(c)}
                        data-testid={`lms-course-option-${c.id}`}
                        className={`w-full text-left px-3 py-2.5 border-b border-slate-100 last:border-b-0 flex items-start gap-2.5 transition-colors ${
                          isSel ? "bg-slate-50" : "hover:bg-slate-50"
                        }`}
                      >
                        <span
                          className={`w-4 h-4 mt-0.5 shrink-0 border rounded-sm flex items-center justify-center ${
                            isSel ? "bg-slate-900 border-slate-900" : "border-slate-300"
                          }`}
                        >
                          {isSel && <Check size={11} className="text-white" />}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-slate-900 truncate">{c.title}</span>
                          {c.description && (
                            <span className="block text-xs text-slate-500 line-clamp-2 mt-0.5">{c.description}</span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="mt-2 text-xs text-slate-500" data-testid="lms-course-count">
        {err
          ? "Catalogue indisponible."
          : loading
          ? "Chargement du catalogue LearnWorlds…"
          : query
          ? `${total} résultat(s) sur ${catalogTotal} cours · ${selected.length} sélectionné(s) · 10 max.`
          : `${total} cours disponible(s) · ${selected.length} sélectionné(s) · 10 max.`}
      </div>
    </div>
  );
}
